import axios from "axios";
import { logErrorFilter, logFilter } from "../logging";
import { appConfig } from "@configs/appConfig";
import { handleExtras } from "@store/extrasHelper";

const serverCache = {};
let authToken = null;
let auditList = [];
const serverStats = { cachehitcount: 0, servercommscount: 0 };

export const addToAudit = (entry) => {
  auditList.push(entry);
};

const createHashKey = (inputdata) => {
  let ret = `hk_${inputdata.topic}${inputdata.action}`;
  if (inputdata.key !== undefined) {
    ret = ret + inputdata.key;
  }
  return ret;
};

export const isLoggedIn = () => {
  if (authToken === null) {
    authToken = localStorage.getItem("authToken");
    // todo look into this for more rigorous check :)
  }
  return authToken !== null;
};

export const authToServer = async (data) => {
  try {
    authToken = null; // reset
    const cfg = {
      method: "post",
      url: `${appConfig.backendurl}/auth`,
      data,
      timeout: 2000,
      headers: { "Content-type": "application/json" },
    };

    const response = await axios(cfg);
    const responseData = response?.data ?? {};

    if (
      typeof responseData === "string" &&
      (responseData?.indexOf("https://accounts.google.com") !== -1 ||
        responseData?.indexOf("https://login.microsoftonline.com") !== -1 ||
        responseData?.indexOf("/login?token=") !== -1)
    ) {
      window.location.href = responseData;
      return;
    }

    if (responseData?.rc === "OK") {
      authToken = response.data.context.token;
      localStorage.setItem("authToken", authToken);
    }
    return { status: response?.statusText, data: responseData };
  } catch (error) {
    logErrorFilter("auth", "Error in Auth");
    logErrorFilter("auth", error);
    return null;
  }
};

export const talkToServer = async (data, router = "data") => {
  logFilter("talkToServer", "Starting talkToServer");
  if (!(router === "public" || isLoggedIn())) {
    logFilter("talkToServer", "No Token, you're not logged in yet");
    window.dispatchEvent(new Event("needToLogin"));
    return null;
  }
  logFilter("talkToServer", data);
  try {
    if (auditList.length > 0) {
      data.context.audit = [...auditList];
      auditList = [];
    }
    const k = createHashKey(data.context);
    if (data.context.useCache === true) {
      if (serverCache.hasOwnProperty(k)) {
        const c = serverCache[k];
        const nowInSeconds = new Date().getTime() / 1000;
        if (nowInSeconds < c.expiresAt) {
          serverStats.cachehitcount++;
          return c.result;
        } else {
          delete serverCache[k];
        }
      }
    }

    const cfg = {
      method: "post",
      url: `${appConfig.backendurl}/${router}`,
      data,
      timeout: 300000,
      headers: {
        "Content-type": "application/json",
        Authorization: `Bearer ${authToken}`,
      },
    };
    logFilter("talkToServerVerbose", "here is the request", cfg);
    const response = await axios(cfg);
    if (response && response.status === 204) {
      serverStats.servercommscount++;
      return { payload: { result: undefined } };
    } else if (response && response.status === 200) {
      serverStats.servercommscount++;
      if (data.context.expiresIn !== undefined) {
        const sc = {
          result: response.data,
          expiresAt: new Date().getTime() / 1000 + data.context.expiresIn,
        };
        serverCache[k] = sc;
      }
      logFilter("talkToServer", "returning", response.data);

      if (response?.data?.context?.extras) {
        handleExtras(response.data.context.extras);
      }

      return response.data;
    } else {
      return {
        rc: "ERROR",
        context: {
          userMessages: ["Error communicating with server", data.context],
          sysMessages: [`response status is ${response.status}`],
        },
        payload: {},
      };
    }
  } catch (error) {
    logErrorFilter("talkToServer", "Error in talkToServer!!");
    logErrorFilter("talkToServer", error);
    if (error?.response?.status === 401) {
      window.dispatchEvent(new Event("needToLogin"));
      return;
    }
    const ret = {
      rc: "ERROR",
      context: {
        userMessages: [`Error communicating with Server`, data.context],
        sysMessages: [error.message, error.stack],
      },
      payload: {},
    };
    window.dispatchEvent(new Event("talkToServerError"));
    return ret;
  }
};

export const impersonateByContactId = async (contact_id) => {
  logFilter("login", "impersonateByContactId");

  const request = {
    context: {
      topic: "contacts",
      action: "impersonateByContactId",
      options: {
        contact_id,
      },
      useCache: false,
    },
  };
  const response = await talkToServer(request);
  logFilter("login", "after impersonateByContactId", response);
  if (!(response && response.rc === "OK")) return undefined;

  const token = response.payload.result.signedtoken;
  logFilter("login", "setting token", token);
  localStorage.setItem("authToken", token);
  return response.payload.result.impersonateInfo;
};

export const logOut = async () => {
  logFilter("login", "Logging Out");
  try {
    const request = {
      context: {
        topic: "contacts",
        action: "logoutByToken",
        options: {
          token: authToken,
        },
        useCache: false,
      },
    };
    logFilter("login", "Logging out", request);
    const response = await talkToServer(request);
    logFilter("login", response);
  } catch (e) {}
  authToken = null;
  localStorage.clear();
};

/*
This pre-fetches a database of search terms for the navSearchBar
*/
export const refreshSearchDB = async (options, defaultSearchResults = []) => {
  const request = {
    context: {
      topic: "search",
      action: "refreshsearchdb",
      options: { id: options.id, usecache: false },
      useCache: false,
      expiresIn: 30,
    },
    payload: {},
  };
  const results = await talkToServer(request);
  logFilter("search", results);
  if (results && results.rc === "OK") {
    return results.payload;
  }
  return defaultSearchResults; // put default search items here
};

const cleanUp = () => {
  logFilter("cache", "clean me up");
  logFilter("cache", serverCache);
  const rightNow = new Date().getTime() / 1000;
  for (const v in serverCache) {
    if (rightNow > serverCache[v].expiresAt) {
      delete serverCache[v];
    }
  }
  setTimeout(cleanUp, 2000);
};

setTimeout(cleanUp, 2000);
